import React, { useEffect, useState } from 'react';
import { Button, Card, Menu, Dropdown, Input } from 'antd';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import MenuSmallIcon from 'components/Icons/MenuSmallIcon';
import StarIcon from 'components/Icons/StarIcon';
import LockIcon from 'components/Icons/LockIcon';
import UnlockIcon from 'components/Icons/UnlockIcon';
import UnStartIcon from 'components/Icons/UnStarIcon';
import { LoadingOutlined } from '@ant-design/icons';
import Loader from 'components/Loader';
import PublishContent from 'pages/Proposal/components/Publish/components/PublishContent';
import helpers from 'helpers';
import { canUserLock, canUserUnlock, getLockedByUserName } from 'helpers/proposal';
import utils from 'utils/utils';
import ThumbnailCreator from 'components/ThumbnailCreator';
import { GET_TEMPLATE } from 'graphql/queries/contentLibraryQueries';

import './TemplateCard.scss';

const TemplateCard = ({
  thumbnail,
  title,
  isStarred,
  templateId,
  templateUserId,
  templateAuid,
  disabledFilter,
  onClickMenuItem,
  config,
  user,
  onDragStart,
  itemEditable,
  setEditValue,
  isLocked,
  variables,
  lockedData,
  lockLoadingMap,
}) => {
  const [publishContent] = ThumbnailCreator({ title, elementId: templateId, type: 'templates' });
  const isLockedByUser = lockedData?.uid === user._id;

  const [templateData, setTemplateData] = useState(null);
  const [showPreview, toggleShowPreview] = useState(false);

  const [getTemplate, { loading }] = useLazyQuery(GET_TEMPLATE, {
    variables: {
      _id: templateId,
    },
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchTemplate }) => {
      if (!templateData) {
        setTemplateData(fetchTemplate);
      }

      if (showPreview) {
        onClickMenuItem({ actionType: 'preview', templateId, templateData: fetchTemplate });
        toggleShowPreview(false);
      }
    },
  });

  useEffect(() => {
    const onResize = () => {
      document.querySelectorAll('.template-thumbnail-container').forEach((item) => {
        const width = item.clientWidth;
        item.style.height = `${width}px`;
        item.firstElementChild.style.transform = `scale(${(width / 900).toFixed(2)})`;
      });
    };
    window.addEventListener('resize', onResize);

    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const isTemplateLocked = lockedData?.value;
  const canLockTemplate = canUserLock(user);
  const canUnlockTemplate = canUserUnlock(user, lockedData);

  const menu = () => {
    const isAdminTemplate = templateUserId === user._id || templateAuid === user._id ? false : true;
    return (
      <Menu className="template-card-menu">
        {!isAdminTemplate && (
          <>
            {lockedData && !isLockedByUser && (
              <Menu.Item className="lock-unlock-item" disabled={!canUnlockTemplate}>
                <div className="content">
                  <LockIcon className="lock-icon" />
                  <span className="content-text">
                    Locked by: <span className="name">{getLockedByUserName(lockedData)}</span>
                  </span>
                </div>
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() => onClickMenuItem({ actionType: 'lock_unlock', templateId })}
              disabled={isTemplateLocked && !canUnlockTemplate ? true : false}>
              {isTemplateLocked ? (
                canUnlockTemplate ? (
                  <>
                    <UnlockIcon className="lock-icon" /> Unlock
                  </>
                ) : null
              ) : canLockTemplate ? (
                <>
                  <LockIcon className="lock-icon" /> Lock
                </>
              ) : null}
            </Menu.Item>
            <Menu.Item
              disabled={isTemplateLocked}
              onClick={() => onClickMenuItem({ actionType: 'duplicate', templateId })}>
              Duplicate
            </Menu.Item>
            <Menu.Item onClick={() => onClickMenuItem({ actionType: 'share', templateId })}>
              Share
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                onClickMenuItem({
                  actionType: itemEditable ? 'save' : 'rename',
                  templateId,
                  templateName: title,
                })
              }
              disabled={isTemplateLocked}>
              {itemEditable ? 'Save' : 'Rename'}
            </Menu.Item>
            <Menu.Item
              disabled={isTemplateLocked}
              onClick={() => onClickMenuItem({ actionType: 'move', templateId })}>
              Move to folder
            </Menu.Item>
            <Menu.Item
              disabled={isTemplateLocked}
              onClick={() => onClickMenuItem({ actionType: 'delete', templateId })}>
              Delete
            </Menu.Item>
          </>
        )}

        {isAdminTemplate && (
          <>
            <Menu.Item
              disabled={isTemplateLocked}
              onClick={() => onClickMenuItem({ actionType: 'duplicate', templateId })}>
              Duplicate
            </Menu.Item>
            <Menu.Item onClick={() => onClickMenuItem({ actionType: 'hideorenable', templateId })}>
              {disabledFilter ? 'Enable' : 'Hide'}
            </Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  useEffect(() => {
    if (!thumbnail) {
      getTemplate();
    }
  }, [getTemplate, thumbnail]);

  return (
    <Card
      className={`content-library-card ${lockLoadingMap?.[templateId] ? 'disabled-card' : ''}`}
      cover={
        loading ? (
          <Loader />
        ) : (
          <>
            <div className="hover-items-container">
              <div className="hover-items" data-html2canvas-ignore>
                <div className="buttons">
                  <>
                    {(user._id === templateUserId || user._id === templateAuid) && !isLocked && (
                      <Button
                        onClick={() => onClickMenuItem({ actionType: 'edit_template', templateId })}
                        className={`edit-template-btn ${isTemplateLocked ? 'disabled' : ''}`}
                        disabled={isTemplateLocked}>
                        Edit Template
                      </Button>
                    )}

                    <Button
                      onClick={() => {
                        toggleShowPreview(true);
                        getTemplate();
                      }}>
                      Preview
                    </Button>

                    <Button
                      disabled={isLocked}
                      className={`use-as-proposal ${isLocked ? 'disabled' : ''}`}
                      onClick={() =>
                        !isLocked
                          ? onClickMenuItem({ actionType: 'use_template', templateId })
                          : null
                      }>
                      Use as Proposal
                    </Button>
                  </>
                </div>
              </div>
            </div>
            {thumbnail ? (
              <img alt={helpers.formatS3Link(thumbnail)} src={helpers.formatS3Link(thumbnail)} />
            ) : templateData && config ? (
              <div className="template-thumbnail-container" ref={publishContent} id={templateId}>
                <div className="template-thumbnail">
                  <PublishContent
                    config={config}
                    prop={templateData}
                    user={user}
                    previewTemplate
                    isThumbnail
                  />
                </div>
              </div>
            ) : (
              <Loader />
            )}
          </>
        )
      }>
      <div className="card-title">
        {isStarred ? (
          <StarIcon
            className="anticon-star"
            onClick={() =>
              onClickMenuItem({
                actionType: 'starOrUnstar',
                templateId,
                starred: false,
              })
            }
          />
        ) : (
          <UnStartIcon
            className="anticon-star"
            onClick={() =>
              onClickMenuItem({
                actionType: 'starOrUnstar',
                templateId,
                starred: true,
              })
            }
          />
        )}
        {!lockLoadingMap?.[templateId] && isTemplateLocked && (
          <div className="locked-item-marker">
            <LockIcon />
          </div>
        )}

        {lockLoadingMap?.[templateId] && <LoadingOutlined className="loading-indicator" />}

        {!isTemplateLocked && itemEditable ? (
          <Input
            defaultValue={title}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onClickMenuItem({ actionType: 'save', templateId });
              }
            }}
            autoFocus
          />
        ) : (
          <span
            className="title"
            title={utils.changeVariable(variables, title, user?.profile?.dateFormat)}
            onDoubleClick={() =>
              user._id === templateUserId ||
              user._id === templateAuid ||
              (user.roles && user.roles[0] === 'manager')
                ? onClickMenuItem({ actionType: 'rename', templateId })
                : {}
            }>
            {utils.changeVariable(variables, title, user?.profile?.dateFormat)}
          </span>
        )}

        <Dropdown overlay={menu} className="menu-icon" trigger={['click']}>
          <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
            <MenuSmallIcon />
          </a>
        </Dropdown>
      </div>
    </Card>
  );
};

TemplateCard.defaultProps = {
  thumbnail: '',
  isStarred: false,
  disabledFilter: false,
  config: {},
  templateAuid: '',
  itemEditable: false,
  isLocked: false,
};

TemplateCard.propTypes = {
  title: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  isStarred: PropTypes.bool,
  templateId: PropTypes.string.isRequired,
  templateUserId: PropTypes.string.isRequired,
  templateAuid: PropTypes.string,
  disabledFilter: PropTypes.bool,
  onClickMenuItem: PropTypes.func.isRequired,
  config: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object).isRequired,
  itemEditable: PropTypes.bool,
  setEditValue: PropTypes.func.isRequired,
  isLocked: PropTypes.bool,
};

export default TemplateCard;
